// CAMBIO MASIVO DE ANALISTA, AGENTE, HANDLE Y ESTADO.
//
// Lissette, 2026-10-20: *"asi como tenemos un cambio masivo de status al
// seleccionar, coloca tambien cambio masivo para analista, handles, agente,
// estado (Florida FL, Georgia GA...)"*. Gemelo de CambiarEstatusModal.jsx.
//
// Analista y Agente son de la POLIZA. Handle (Atiende) y Estado son del
// CLIENTE: cambiarlos cambia la ficha y por tanto todas sus polizas, y la
// pantalla lo dice antes de aplicar. El trabajo lo hace POST
// /policies/bulk-fields (policies.routes.ts).

import { useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import { Modal } from './ui.jsx';
import { bulkUpdatePolicyFields } from '../api.js';
import { toast } from './Toast.jsx';
import { STATES_US } from '../estadosUsa.js';
import { attendOptions, seatLabelFor } from '../atiende.js';

const CAMPOS = [
  { id: 'analyst', es: 'Analista', en: 'Analyst', delCliente: false },
  { id: 'agent', es: 'Agente en la póliza', en: 'Agent on policy', delCliente: false },
  { id: 'attends', es: 'Handle (Atiende)', en: 'Handle (Attends)', delCliente: true },
  { id: 'state', es: 'Estado (FL, GA…)', en: 'State (FL, GA…)', delCliente: true },
];

export function CambioMasivoModal({
  policies = [], users = [], seatLabels = {}, analystNames = [], agentNames = [],
  lang = 'es', apiMode = true, onClose, onDone,
}) {
  const es = lang === 'es';
  const [campo, setCampo] = useState('');
  const [valor, setValor] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const def = CAMPOS.find((c) => c.id === campo);
  const clientes = useMemo(() => new Set(policies.map((p) => p.clientId).filter(Boolean)).size, [policies]);
  const puede = !!campo && !!String(valor).trim() && !ocupado;

  const aplicar = async () => {
    if (!puede) return;
    setOcupado(true);
    const ids = policies.map((p) => p.id);
    const v = String(valor).trim();
    try {
      let cambiadas = 0; let sinCambio = 0; let fuera = 0; let tocados = 0;
      if (apiMode) {
        for (let i = 0; i < ids.length; i += 200) {
          const r = await bulkUpdatePolicyFields(ids.slice(i, i + 200), campo, v);
          cambiadas += r.cambiadas; sinCambio += r.sinCambio; fuera += r.fuera; tocados += r.clientes;
        }
      } else {
        cambiadas = def.delCliente ? 0 : ids.length; tocados = def.delCliente ? clientes : 0;
      }
      // Lo que CONTESTO el servidor, no lo que se penso mandar (regla 142).
      const partes = def.delCliente
        ? [es ? `${tocados} cliente(s) cambiado(s)` : `${tocados} client(s) changed`]
        : [es ? `${cambiadas} póliza(s) cambiada(s)` : `${cambiadas} policy(ies) changed`];
      if (sinCambio) partes.push(es ? `${sinCambio} ya estaba(n) así` : `${sinCambio} already had it`);
      if (fuera) partes.push(es ? `${fuera} fuera de tu alcance` : `${fuera} outside your scope`);
      toast.success(partes.join(' · '));
      if (typeof onDone === 'function') onDone({ ids, campo, valor: v });
      onClose();
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo hacer el cambio' : 'Could not apply the change'));
    } finally {
      setOcupado(false);
    }
  };

  const listaDeTexto = campo === 'analyst' ? analystNames : agentNames;

  return (
    <Modal title={es ? 'Cambio masivo de las seleccionadas' : 'Bulk change selected'} onClose={() => !ocupado && onClose()}>
      <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.9rem' }}>
        {es ? `${policies.length} póliza(s) seleccionada(s)` : `${policies.length} policy(ies) selected`}
      </div>

      <label className="dkt-input-lbl">{es ? 'Qué cambiar' : 'What to change'}</label>
      <select className="dkt-inp" value={campo} onChange={(e) => { setCampo(e.target.value); setValor(''); }}>
        <option value="">{es ? '— elegir —' : '— pick —'}</option>
        {CAMPOS.map((c) => <option key={c.id} value={c.id}>{es ? c.es : c.en}</option>)}
      </select>

      {!!campo && (
        <div style={{ marginTop: '.8rem' }}>
          <label className="dkt-input-lbl">{es ? 'Nuevo valor' : 'New value'}</label>
          {(campo === 'analyst' || campo === 'agent') && <>
            <input className="dkt-inp" list="dkt-cambio-masivo-lista" value={valor} onChange={(e) => setValor(e.target.value)}
              placeholder={es ? 'Escribe o elige un nombre' : 'Type or pick a name'} />
            <datalist id="dkt-cambio-masivo-lista">
              {listaDeTexto.map((n) => <option key={n} value={n} />)}
            </datalist>
          </>}
          {campo === 'attends' && (
            <select className="dkt-inp" value={valor} onChange={(e) => setValor(e.target.value)}>
              <option value="">{es ? '— elegir persona —' : '— pick a person —'}</option>
              {attendOptions(users).map((u) => <option key={u.id} value={u.id}>{seatLabelFor(u.id, seatLabels, users)?.name}</option>)}
            </select>
          )}
          {campo === 'state' && (
            <select className="dkt-inp" value={valor} onChange={(e) => setValor(e.target.value)}>
              <option value="">{es ? '— elegir estado —' : '— pick a state —'}</option>
              {STATES_US.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
      )}

      {def?.delCliente && (
        <div style={{
          marginTop: '.8rem', padding: '.6rem .7rem', borderRadius: 8, fontSize: '.72rem', lineHeight: 1.55,
          background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.35)', color: 'var(--c-amber,#f59e0b)',
        }}>
          {es
            ? `Este dato es del CLIENTE, no de la póliza: se cambia en la ficha de ${clientes} cliente(s) y se ve en todas sus pólizas.`
            : `This belongs to the CLIENT, not the policy: it changes on ${clientes} client record(s) and shows on all their policies.`}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '1.1rem' }}>
        <button type="button" className="dkt-btn-g" onClick={onClose} disabled={ocupado}>
          {es ? 'Cancelar' : 'Cancel'}
        </button>
        <button type="button" className="dkt-btn-p" onClick={aplicar} disabled={!puede}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: puede ? 1 : 0.5 }}>
          <Users size={14} />
          {ocupado ? (es ? 'Cambiando…' : 'Changing…') : (es ? 'Aplicar' : 'Apply')}
        </button>
      </div>
    </Modal>
  );
}
